import React, { useState } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Badge,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  People as PeopleIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useWorkspace } from '../contexts/WorkspaceContext';

interface WorkspaceItem {
  id: string;
  name: string;
  display_name: string;  // Company name from personalization (or fallback to name)
  slug: string;
  member_role: string;
  member_count: number;
  is_owner: boolean;
  is_current: boolean;
  unread_messages?: number;
  logo?: string;
}

interface WorkspaceSwitcherProps {
  companyName: string;
  logoUrl?: string;
  showLogo?: boolean;
  borderRadius?: string;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({
  companyName,
  logoUrl,
  showLogo = true,
  borderRadius = '8px',
}) => {
  const { workspace, refreshWorkspace } = useWorkspace();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const open = Boolean(anchorEl);

  // Load workspaces when menu opens
  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    await loadWorkspaces();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadWorkspaces = async () => {
    try {
      setLoading(true);
      const result = await apiService.getMyWorkspaces();
      setWorkspaces(result.workspaces);
    } catch (error) {
      console.error('Failed to load workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchWorkspace = async (workspaceId: string) => {
    if (workspaceId === workspace?.id) {
      handleClose();
      return;
    }

    try {
      setSwitching(true);
      await apiService.switchWorkspace(workspaceId);
      
      // Clear all cached data
      localStorage.removeItem('personalization-settings');
      
      // Refresh workspace context - this will reload all data
      await refreshWorkspace();
      
      // Close menu
      handleClose();
      
      // Force page reload to ensure all components update
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch workspace:', error);
      setSwitching(false);
    }
  };

  const getTotalUnread = () => {
    return workspaces
      .filter((ws: WorkspaceItem) => !ws.is_current)
      .reduce((sum: number, ws: WorkspaceItem) => sum + (ws.unread_messages || 0), 0);
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  return (
    <>
      {/* Company Name / Workspace Switcher Button */}
      <Badge 
        badgeContent={getTotalUnread()} 
        color="error"
        invisible={getTotalUnread() === 0}
      >
        <Box
          onClick={handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {/* Logo */}
          {showLogo && (
            <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
              {logoUrl && !logoError ? (
                <img 
                  src={logoUrl} 
                  alt={`${companyName} logo`}
                  onError={handleLogoError}
                  style={{ 
                    height: '32px', 
                    width: '32px',
                    objectFit: 'contain',
                    borderRadius: parseInt(borderRadius.replace('px', '')) / 2,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: parseInt(borderRadius.replace('px', '')) / 2,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                  }}
                >
                  {companyName.charAt(0).toUpperCase()}
                </Box>
              )}
            </Box>
          )}
          
          {/* Company Name */}
          <Typography
            variant="h6"
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              color: 'white',
              fontFamily: 'var(--heading-font, inherit)',
              display: { xs: showLogo ? 'none' : 'block', sm: 'block' }
            }}
          >
            {companyName}
          </Typography>
          
          {/* Dropdown Arrow */}
          <ExpandMoreIcon sx={{ ml: 0.5, color: 'white', fontSize: 20 }} />
        </Box>
      </Badge>

      {/* Workspace Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            maxWidth: 360,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Your Workspaces
          </Typography>
        </Box>

        <Divider />

        {/* Loading state */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Workspace list */}
            {workspaces.map((ws: WorkspaceItem) => (
              <MenuItem
                key={ws.id}
                onClick={() => handleSwitchWorkspace(ws.id)}
                selected={ws.is_current}
                disabled={switching}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                  },
                }}
              >
                <ListItemIcon>
                  <Badge
                    badgeContent={ws.unread_messages || 0}
                    color="error"
                    invisible={!ws.unread_messages || ws.is_current}
                  >
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        bgcolor: ws.is_current ? 'primary.main' : 'grey.400',
                        fontSize: 14,
                      }}
                    >
                      {(ws.display_name || ws.name).charAt(0).toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={ws.is_current ? 600 : 400}>
                        {ws.display_name || ws.name}
                      </Typography>
                      {ws.is_owner && (
                        <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon sx={{ fontSize: 12 }} />
                      <Typography variant="caption" color="text.secondary">
                        {ws.member_count} {ws.member_count === 1 ? 'member' : 'members'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        • {ws.member_role}
                      </Typography>
                    </Box>
                  }
                />
                {ws.is_current && (
                  <CheckIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                )}
              </MenuItem>
            ))}

            {workspaces.length === 0 && (
              <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No workspaces found
                </Typography>
              </Box>
            )}
          </>
        )}
      </Menu>
    </>
  );
};

export default WorkspaceSwitcher;
